import { maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  FEATURE_FLAGS_WIRE_KEY,
  parseFeatureFlagsResponse,
  toFeatureFlagSet
} from 'store/teams/featureFlags.bridge';
import { ReduxProxy } from 'v5/api/_common/reduxProxy';
import {
  EnterpriseFeatureFlagApi,
  FeatureFlagSet
} from 'v5/api/enterprise-feature-flag/enterprise-feature-flag.api';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { EnterpriseFeatureFlagService } from './EnterpriseFeatureFlagService';

export function createEnterpriseFeatureFlagService(config: {
  api: EnterpriseFeatureFlagApi;
  queryClient: QueryClient;
  reduxProxy: ReduxProxy;
}): EnterpriseFeatureFlagServiceInstance {
  const { api, queryClient, reduxProxy } = config;

  const flagSetQueryKey = (organizationId: string) =>
    ['enterprise-feature-flags-v5', organizationId] as const;

  function seedCache(organizationId: string, set: FeatureFlagSet) {
    queryClient.setQueryData(flagSetQueryKey(organizationId), set);
  }

  // Never stale: flags are read once per session and every write goes through
  // seedCache, so refetching would only discard the value we just stored.
  function flagSetQuery(organizationId: string) {
    return {
      queryKey: flagSetQueryKey(organizationId),
      queryFn: () => api.getFlagSet(organizationId),
      staleTime: Infinity
    };
  }

  // Seed TanStack from teams bootstrap wire stash on getAccountTeams/fulfilled.
  // Redux no longer mirrors flag writes; FeatureService reads scoped flags only.
  const unsubscribeFromTeams = reduxProxy.read.addListener(event => {
    if (event.type !== 'teams/getAccountTeams/fulfilled') {
      return;
    }
    for (const team of event.payload) {
      const wire =
        team[FEATURE_FLAGS_WIRE_KEY] ??
        parseFeatureFlagsResponse(team.featureFlags);
      seedCache(team.dbSchema, toFeatureFlagSet(wire));
    }
  });

  const service: EnterpriseFeatureFlagService = {
    useFlagSet: organizationId =>
      ensureIdleQuery(
        organizationId,
        useQuery(
          {
            enabled: maybe.isPresent(organizationId),
            ...flagSetQuery(organizationId!)
          },
          queryClient
        )
      ),

    ensureFlagSet: organizationId =>
      queryClient.fetchQuery(flagSetQuery(organizationId)),

    saveFlagSet: async (organizationId, next) => {
      await api.saveFlagSet(organizationId, next);
      seedCache(organizationId, next);
    }
  };

  return Object.assign(service, { dispose: unsubscribeFromTeams });
}

export type EnterpriseFeatureFlagServiceInstance =
  EnterpriseFeatureFlagService & {
    dispose: () => void;
  };
